import styles from "./PaymentStripeForm.module.scss";
import { Dispatch, FC, FormEvent, FormEventHandler, MouseEvent, SetStateAction, useEffect, useRef, useState } from "react";
import { userDataState, lighterBackgroundColorState, themeState, codeAppliedState, promoCodeDisabledState } from "@/lib/store";
import { ExpressCheckoutElement, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import type { PaymentIntentResult, StripeExpressCheckoutElementConfirmEvent, StripeExpressCheckoutElementReadyEvent } from "@stripe/stripe-js";
import { useRecoilState, useRecoilValue } from "recoil";
import { cancelBasic } from "@/controllers/subscription";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleLeft } from "@fortawesome/pro-solid-svg-icons";
import { Plan } from "@/models/subscription";
import { classNames } from "@/lib/utils";
import useApplyPromoCode from "src/hooks/useApplyPromoCode";
import SnackBar from "../snack-bar/SnackBar";
import { useRouter } from "next/router";

interface PropsTypes {
    payment: {};
    setStep: (step: number) => void;
    disabled: boolean;
    setDisabled: Dispatch<SetStateAction<boolean>>;
    firstNameInvalid: boolean;
    setFirstNameInvalid: Dispatch<SetStateAction<boolean>>;
    lastNameInvalid: boolean;
    setLastNameInvalid: Dispatch<SetStateAction<boolean>>;
    plan: Plan;
}

const PaymentStripeForm: FC<PropsTypes> = ({ payment, setStep, disabled, setDisabled, firstNameInvalid, setFirstNameInvalid, lastNameInvalid, setLastNameInvalid, plan }) => {
    const stripe = useStripe();
    const elements = useElements();
    const { query } = useRouter();

    const userData = useRecoilValue(userDataState);
    const theme = useRecoilValue(themeState);
    const lighterBackground = useRecoilValue(lighterBackgroundColorState);
    const [card, setCard] = useState<{ brand: string; last4: string }>();
    // Which one-tap methods the Express Checkout Element could show in this browser (Apple Pay
    // in Safari with a card in Wallet, Google Pay in Chrome, Link for returning Link users).
    const [expressMethods, setExpressMethods] = useState<string[]>([]);

    const firstNameRef = useRef<HTMLInputElement>();
    const lastNameRef = useRef<HTMLInputElement>();
    const discountCodeRef = useRef<HTMLInputElement>();
    const firstLoad = useRef(true);

    const isDisabled = disabled || firstNameInvalid || lastNameInvalid;

    const [promoCodeDisabled, setPromoCodeDisabled] = useRecoilState(promoCodeDisabledState);
    const codeApplied = useRecoilValue(codeAppliedState);
    const [promoCode, setPromoCode] = useState(codeApplied);
    const hasPromoParam = query.hasOwnProperty("promo");

    const [snackBar, setSnackBar] = useState<{ open: boolean; text: string; severity: "success" | "error" | "info" | "warning" }>({
        open: false,
        text: "",
        severity: "success",
    });

    const { applyPromoCode } = useApplyPromoCode(setSnackBar);

    useEffect(() => {
        setPromoCodeDisabled(!promoCode.length);
    }, [promoCode]);

    useEffect(() => {
        const promoCodeFromQuery = query.promo as string;
        const promoCodeFromSession = sessionStorage.getItem("promoCode");

        // Priority: URL parameter > sessionStorage > codeApplied state
        if (promoCodeFromQuery && promoCodeFromQuery !== "true") {
            setPromoCode(promoCodeFromQuery);
        } else if (!promoCode && promoCodeFromSession && promoCodeFromSession !== "true") {
            // If no promo code is set yet and sessionStorage has a valid promo code, use it
            setPromoCode(promoCodeFromSession);
        }
    }, [query.promo]);

    useEffect(() => {
        if (!card && userData) {
            firstNameRef.current.value = userData?.first_name;
            lastNameRef.current.value = userData?.last_name;
        }
    }, [card, userData]);

    useEffect(() => {
        if (firstLoad.current) firstLoad.current = false;
        else onSubmit();
    }, [payment]);

    // Shared by the card form and the one-tap wallets: record the purchase and move on.
    const handlePaymentResult = (res: PaymentIntentResult) => {
                    if (res.error) setSnackBar({ open: true, text: res.error.message, severity: "error" });
                    else {
                        try {
                            (window as any).dataLayer.push({
                                event: "purchase",
                                user_id: userData?.user_id ?? null,
                                ecommerce: {
                                    transaction_id: res.paymentIntent.id,
                                    value: plan.plan_price,
                                    currency: plan.plan_currency,
                                    items: [
                                        {
                                            item_id: `plan-${plan.plan_id}`,
                                            item_name: `plan-${plan.plan_id}-${plan.plan_interval}`,
                                            price: plan.plan_price,
                                            quantity: 1,
                                        },
                                    ],
                                },
                            });
                        } catch (err) {
                            console.error(err);
                        }

                        // Push subscription_paid event to dataLayer for GA4
                        try {
                            (window as any).dataLayer.push({
                                event: "subscription_paid",
                                user_id: userData?.user_id ?? null,
                                transaction_id: userData.email,
                                value: plan.plan_price,
                                currency: plan.plan_currency,
                                product_name: plan.product_name,
                            });
                        } catch (err) {
                            console.error("Error pushing subscription_paid to dataLayer:", err);
                        }

                        setStep(3);
                    }
    };

    const doPayment = () => {
        if (process.env.NEXT_PUBLIC_VERCEL_ENV)
            stripe
                .confirmPayment({
                    elements,
                    redirect: "if_required",
                    confirmParams: {
                        payment_method_data: {
                            billing_details: {
                                email: userData.email,
                                name: `${firstNameRef.current?.value} ${lastNameRef.current?.value}`,
                            },
                        },
                    },
                })
                .then(handlePaymentResult)
                .finally(() => setDisabled(false));
        else setStep(3);
    };

    // Apple Pay, Google Pay or Link confirmed in the wallet sheet: the wallet supplies the
    // name and card, so the name fields below are not required on this path.
    const onExpressConfirm = async (event: StripeExpressCheckoutElementConfirmEvent) => {
        setDisabled(true);
        try {
            if (userData.subscription_plan === "ORTEX Basic") await cancelBasic();
            const res = await stripe.confirmPayment({
                elements,
                redirect: "if_required",
                confirmParams: {
                    payment_method_data: {
                        billing_details: {
                            email: userData.email,
                            name: event.billingDetails?.name || undefined,
                        },
                    },
                },
            });
            handlePaymentResult(res);
        } catch (err) {
            console.error("Express checkout failed:", err);
            setSnackBar({ open: true, text: "The payment could not be completed. Please try again or pay by card below.", severity: "error" });
        } finally {
            setDisabled(false);
        }
    };

    const onExpressReady = (event: StripeExpressCheckoutElementReadyEvent) => {
        const available = Object.entries(event.availablePaymentMethods ?? {})
            .filter(([, on]) => on)
            .map(([name]) => name);
        setExpressMethods(available);
    };

    const onSubmit = (e?: MouseEvent | FormEvent) => {
        e?.preventDefault();
        setDisabled(true);
        if (userData.subscription_plan === "ORTEX Basic")
            cancelBasic()
                .then(doPayment)
                .catch(() => setDisabled(false));
        else doPayment();
    };

    const onInvalid: FormEventHandler<HTMLFormElement> = (e) => {
        if ((e.target as HTMLInputElement).id === "firstName") setFirstNameInvalid(true);
        if ((e.target as HTMLInputElement).id === "lastName") setLastNameInvalid(true);
    };

    return (
        <div className={styles.PaymentStripeForm}>
            <form onSubmit={onSubmit} onInvalid={onInvalid}>
                {!userData?.app_subscriber_only && (
                    <button className={styles.backBtn} onClick={() => setStep(1)}>
                        <FontAwesomeIcon icon={faAngleLeft} color="#808080" height={30} />
                        Go back
                    </button>
                )}
                {card ? (
                    <div className={styles.card}>
                        <p>
                            The subscription will be charged to your {card.brand.toUpperCase()} card that ends with {card.last4}
                        </p>
                        <button className={styles.card} onClick={() => setCard(null)} style={{ border: `2px solid ${lighterBackground}` }}>
                            Change Card
                        </button>
                    </div>
                ) : (
                    <>
                        <div className={styles.express} data-testid="express-checkout" data-methods={expressMethods.join(",")}>
                            <ExpressCheckoutElement
                                onConfirm={onExpressConfirm}
                                onReady={onExpressReady}
                                options={{
                                    buttonType: { applePay: "subscribe", googlePay: "subscribe" },
                                    buttonHeight: 44,
                                    paymentMethodOrder: ["apple_pay", "google_pay", "link"],
                                    layout: { maxColumns: 1, overflow: "never" },
                                }}
                            />
                            {expressMethods.length > 0 && (
                                <div className={styles.expressDivider}>
                                    <span>or pay by card</span>
                                </div>
                            )}
                        </div>
                        <div>
                            <label>
                                First Name
                                <input
                                    id="firstName"
                                    placeholder="John"
                                    ref={firstNameRef}
                                    required
                                    className={classNames(styles[firstNameInvalid && "invalid"], theme === "dark" ? styles.dark : styles.light)}
                                    onChange={(e) => setFirstNameInvalid(!e.target.checkValidity())}
                                />
                            </label>
                            <label>
                                Last Name
                                <input
                                    id="lastName"
                                    placeholder="Doe"
                                    ref={lastNameRef}
                                    required
                                    className={classNames(styles[lastNameInvalid && "invalid"], theme === "dark" ? styles.dark : styles.light)}
                                    onChange={(e) => setLastNameInvalid(!e.target.checkValidity())}
                                />
                            </label>

                            {(hasPromoParam || codeApplied || promoCode) && !(userData.app_subscriber_only || promoCode?.toUpperCase() === "FEMALEINVEST") ? (
                                <div className={styles.promo}>
                                    <label>
                                        Promo code
                                        <input
                                            id="discountCode"
                                            ref={discountCodeRef}
                                            value={promoCode}
                                            onChange={(e) => setPromoCode(e.target.value)}
                                            onKeyDown={(e) => {
                                                e.key === "Enter" && (e.preventDefault(), applyPromoCode(promoCode));
                                            }}
                                            placeholder={promoCode || "Enter promo code"}
                                            className={classNames(theme === "dark" ? styles.dark : styles.light)}
                                        />
                                    </label>
                                    <button className={styles.applyPromoBtn} type="button" onClick={() => applyPromoCode(promoCode)} disabled={promoCodeDisabled}>
                                        Apply
                                    </button>
                                </div>
                            ) : (
                                <></>
                            )}
                        </div>
                        <PaymentElement
                            onChange={(e) => setDisabled(!e.complete)}
                            options={{
                                layout: "tabs",
                                paymentMethodOrder: ["card", "link"],
                                // The wallets have their own buttons above; not twice.
                                wallets: { applePay: "never", googlePay: "never" },
                            }}
                        />

                        <button type="button" disabled={isDisabled} onClick={onSubmit}>
                            Complete your purchase
                        </button>
                    </>
                )}
            </form>
            <SnackBar open={snackBar.open} setOpen={(open) => setSnackBar({ ...snackBar, open })} severity={snackBar.severity} text={snackBar.text} />
        </div>
    );
};

export default PaymentStripeForm;
